export type PublicPageMetadata = Readonly<{
  title: string;
  description: string;
  canonicalUrl: string;
  robots: Readonly<{
    content: string;
    index: boolean;
    follow: boolean;
  }>;
  og: Readonly<{
    type: string;
    locale: string;
    siteName: string;
    title: string;
    description: string;
    url: string;
    image: string;
    imageAlt: string;
  }>;
  twitter: Readonly<{
    card: string;
    title: string;
    description: string;
    image: string;
    imageAlt: string;
  }>;
  sitemap: Readonly<{
    include: boolean;
  }>;
}>;

type PublicPageMetadataInput = {
  title: string;
  description: string;
  canonicalUrl: string;
  image: string;
  imageAlt: string;
  ogType?: string;
  ogLocale?: string;
  ogSiteName?: string;
  twitterCard?: string;
  robotsContent: string;
  robotsIndex: boolean;
  robotsFollow: boolean;
  includeInSitemap: boolean;
};

function definePublicPageMetadata({
  title,
  description,
  canonicalUrl,
  image,
  imageAlt,
  ogType = "website",
  ogLocale = "ja_JP",
  ogSiteName = "WizMate",
  twitterCard = "summary_large_image",
  robotsContent,
  robotsIndex,
  robotsFollow,
  includeInSitemap,
}: PublicPageMetadataInput): PublicPageMetadata {
  return Object.freeze({
    title,
    description,
    canonicalUrl,
    robots: Object.freeze({
      content: robotsContent,
      index: robotsIndex,
      follow: robotsFollow,
    }),
    og: Object.freeze({
      type: ogType,
      locale: ogLocale,
      siteName: ogSiteName,
      title,
      description,
      url: canonicalUrl,
      image,
      imageAlt,
    }),
    twitter: Object.freeze({
      card: twitterCard,
      title,
      description,
      image,
      imageAlt,
    }),
    sitemap: Object.freeze({
      include: includeInSitemap,
    }),
  });
}

const LANDING_TITLE = "WizMate｜社長の毎日に、AI社長室を。";
const LANDING_DESCRIPTION =
  "WizMateは、社長の悩み・判断・調べもの・日々の雑務を、メイとAIチームに頼めるAI社長室です。AI壁打ち、企業リサーチ、ナレッジ活用を無料で始められます。";
const SALES_SUPPORT_TITLE = "WizMate営業支援｜商談前の調べる・考えるを、AIと一緒に";
const SALES_SUPPORT_DESCRIPTION =
  "相手企業を調べ、自社の提案と照らし、AI専門家と商談前に壁打ち。AI社長室WizMateで営業準備を一つの流れにまとめます。";
const PRESIDENT_CONSULT_TITLE = "WizMate社長相談｜正解のない経営判断を、AIと壁打ち";
const PRESIDENT_CONSULT_DESCRIPTION =
  "まとまっていない相談から始め、相談内容に合ったAIメンバーとスタンスを開始前に選んで壁打ち。AI社長室WizMateが、社長自身の意思決定を支えます。";
const KNOWLEDGE_USE_TITLE = "WizMateナレッジ活用｜紙・PDF・Webを、AIと使える仕事の材料に";
const KNOWLEDGE_USE_DESCRIPTION =
  "紙資料を撮影した社長メモ、PDF、Webページをナレッジライブラリへ。必要な情報を選び、企業分析やAI壁打ちの材料として活用できます。";

export const PUBLIC_PAGE_METADATA: Readonly<Record<string, PublicPageMetadata>> = Object.freeze({
  landing: definePublicPageMetadata({
    title: LANDING_TITLE,
    description: LANDING_DESCRIPTION,
    canonicalUrl: "https://wizmate.jp/",
    image: "https://wizmate.jp/og/wizmate-landing.jpg",
    imageAlt: LANDING_TITLE,
    robotsContent: "index,follow,max-image-preview:large",
    robotsIndex: true,
    robotsFollow: true,
    includeInSitemap: true,
  }),
  salesSupport: definePublicPageMetadata({
    title: SALES_SUPPORT_TITLE,
    description: SALES_SUPPORT_DESCRIPTION,
    canonicalUrl: "https://wizmate.jp/sales-support",
    image: "https://wizmate.jp/og/wizmate-landing.jpg",
    imageAlt: SALES_SUPPORT_TITLE,
    robotsContent: "noindex,follow",
    robotsIndex: false,
    robotsFollow: true,
    includeInSitemap: false,
  }),
  presidentConsult: definePublicPageMetadata({
    title: PRESIDENT_CONSULT_TITLE,
    description: PRESIDENT_CONSULT_DESCRIPTION,
    canonicalUrl: "https://wizmate.jp/president-consult",
    image: "https://wizmate.jp/og/wizmate-landing.jpg",
    imageAlt: PRESIDENT_CONSULT_TITLE,
    robotsContent: "noindex,follow",
    robotsIndex: false,
    robotsFollow: true,
    includeInSitemap: false,
  }),
  knowledgeUse: definePublicPageMetadata({
    title: KNOWLEDGE_USE_TITLE,
    description: KNOWLEDGE_USE_DESCRIPTION,
    canonicalUrl: "https://wizmate.jp/knowledge-use",
    image: "https://wizmate.jp/og/wizmate-landing.jpg",
    imageAlt: KNOWLEDGE_USE_TITLE,
    robotsContent: "noindex,follow",
    robotsIndex: false,
    robotsFollow: true,
    includeInSitemap: false,
  }),
});
